
import { useState, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { uploadFile, uploadFolder } from "@/services/fileUploadService";
import { supabase } from "@/integrations/supabase/client";

interface UploadedFile {
  filename: string;
  id: string;
  linkId?: string;
}

export function useFileUpload() {
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [customName, setCustomName] = useState("");
  const [uploadComplete, setUploadComplete] = useState(false);
  const [currentTotalSize, setCurrentTotalSize] = useState(0);
  const { toast } = useToast();

  const handleDrop = useCallback((acceptedFiles: File[]) => {
    setFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
  }, []);

  const removeFile = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleCustomNameChange = (value: string) => {
    setCustomName(value);
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      toast({
        title: "Error",
        description: "Please select a file to upload.",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    setUploadComplete(false);
    setProgress(0);
    setUploadedFiles([]);

    try {
      const totalSize = files.reduce((acc, file) => acc + file.size, 0);
      setCurrentTotalSize(totalSize);

      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        throw new Error("User not authenticated");
      }

      if (files.length === 1) {
        const file = files[0];
        const uploadResult = await uploadFile(file, user.id, (progress) => {
          setProgress(progress);
        });

        console.log("Upload completed, setting state", uploadResult);
        setUploadedFiles([{
          filename: uploadResult.filename,
          id: uploadResult.id,
          linkId: uploadResult.linkId
        }]);
        setUploadComplete(true);

        toast({
          title: "Success",
          description: "File uploaded successfully.",
        });
      } else {
        const uploadResult = await uploadFolder(files, customName || 'unnamed', user.id, (progress) => {
          setProgress(progress);
        });

        console.log("Folder upload completed, setting state", uploadResult);
        setUploadedFiles([{
          filename: uploadResult.filename,
          id: uploadResult.id,
          linkId: uploadResult.linkId
        }]);
        setUploadComplete(true);

        toast({
          title: "Success",
          description: "Folder uploaded successfully.",
        });
      }
    } catch (error: any) {
      console.error("Upload error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to upload file.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const copyToClipboard = async (filePath: string, transferId: string) => {
    try {
      console.log("Copying to clipboard", { filePath, transferId });
      const file = uploadedFiles.find(f => f.id === transferId);
      
      if (!file?.linkId) {
        throw new Error("No se encontró el enlace de descarga");
      }

      const downloadUrl = `${window.location.origin}/download?id=${file.linkId}`;
      await navigator.clipboard.writeText(downloadUrl);
      
      toast({
        title: "¡Enlace copiado!",
        description: "El enlace de descarga ha sido copiado al portapapeles",
      });
    } catch (error: any) {
      console.error("Error al copiar enlace:", error);
      toast({
        title: "Error",
        description: error.message || "No se pudo copiar el enlace",
        variant: "destructive",
      });
    }
  };

  const resetUpload = () => {
    console.log("Resetting upload state");
    setFiles([]);
    setUploading(false);
    setProgress(0);
    setUploadedFiles([]);
    setCustomName("");
    setUploadComplete(false);
    setCurrentTotalSize(0);
  };

  return {
    files,
    uploading,
    progress,
    uploadedFiles,
    customName,
    uploadComplete,
    currentTotalSize,
    removeFile,
    handleDrop,
    copyToClipboard,
    handleUpload,
    resetUpload,
    handleCustomNameChange
  };
}
